/**
 * Choices left for one Facebook target location row, given the other rows.
 * Mirrors the backend's `FacebookLocationTargeting` rules, which reject a country targeted twice,
 * a city/region/place inside a country targeted as a whole, and the same city/region/place twice.
 */
import type { IFacebookCountry, IFacebookTargeting } from '@/types/locationTargeting';

/**
 * Countries the row at `index` can still pick. The row's own country always stays, so its current
 * selection keeps showing even when it already conflicts with another row.
 */
export function countriesForRow(
  countries: IFacebookCountry[],
  locations: Partial<IFacebookTargeting>[],
  index: number
): IFacebookCountry[] {
  const row = locations[index];
  const isCountryRow = row?.target_type === 'country';
  const taken = new Set<string>();

  locations.forEach((other, otherIndex) => {
    if (otherIndex === index || !other.target_country) return;
    // a whole-country row conflicts with any row in that country, a city/region/place row only with whole-country rows
    if (isCountryRow || other.target_type === 'country') taken.add(other.target_country);
  });

  return countries.filter(
    (country) => country.country_code === row?.target_country || !taken.has(country.country_code)
  );
}

/** Keys of the cities, regions and places targeted by every row except the one at `index`. */
export function locationKeysOfOtherRows(locations: Partial<IFacebookTargeting>[], index: number): string[] {
  return locations
    .filter((other, otherIndex) => otherIndex !== index && other.target_type !== 'country' && !!other.target_key)
    .map((other) => other.target_key as string);
}
